import { NovelsMiddleware } from '../middlewares/novels';
import { Action } from '../actions';
import { StoreState } from '../state';

import { ActionType as LocationActionType } from '../actions/location';
import { ActionType as TruckActionType } from '../actions/truck';
import { ActionType as TruckTemplateActionType } from '../actions/truck-template';
import { ActionType as DocumentActionType } from '../actions/document';
import { ActionType as LinehaulCompanyActionType } from '../actions/linehaul-company';
import { ActionType as AuthActionType } from '../actions/auth';
import { ActionType as CustomsBrokerActionType } from '../actions/customs-broker';
import { ActionType as LoadingRestrictionActionType } from '../actions/loading-restriction';
import { ActionType as ShipmentGroupActionType } from '../actions/shipment-group';
import { ActionType as ManifestActionType } from '../actions/manifest';
import { ActionType as FeatureFlagsActionType } from '../actions/feature';

import { authNovelsCreator, AuthNovels } from './auth.novels';
import { customsBrokerNovelsCreator, CustomsBrokerNovels } from './customs-broker.novels';
import { loadingRestrictionNovelsCreator, LoadingRestrictionNovels } from './loading-restriction.novels';
import { documentNovelsCreator, DocumentNovels } from './document.novels';
import { locationNovelsCreator, LocationNovels } from './location.novels';
import { linehaulCompanyNovelsCreator, LinehaulCompanyNovels } from './linehaul-company.novels';
import { truckNovelsCreator, TruckNovels } from './truck.novels';
import { shipmentGroupNovelsCreator, ShipmentGroupNovels } from './shipment-group.novels';
import { manifestNovelsCreator, ManifestNovels } from './manifest.novels';
import { FeatureFlagsNovels, featureFlagsNovelsCreator } from './feature-flags.novels';

import { AppServices } from '../../app.services';
import { TruckTemplateNovels, truckTemplateNovelsCreator } from './truck-template.novels';

export interface AppNovels {
  authNovels: AuthNovels;
  customsBrokerNovels: CustomsBrokerNovels;
  loadingRestrictionNovels: LoadingRestrictionNovels;
  locationNovels: LocationNovels;
  linehaulCompanyNovels: LinehaulCompanyNovels;
  truckNovels: TruckNovels;
  documentNovels: DocumentNovels;
  shipmentGroupNovels: ShipmentGroupNovels;
  manifestNovels: ManifestNovels;
  featureFlagsNovels: FeatureFlagsNovels;
  truckTemplateNovels: TruckTemplateNovels;
}

const createAppNovels = (services: AppServices): AppNovels => {
  const authNovels = authNovelsCreator(services.authService);
  const truckNovels = truckNovelsCreator(services.truckService, services.providers.locationProvider);
  const locationNovels = locationNovelsCreator(services.locationService, services.persistenceService);
  const linehaulCompanyNovels = linehaulCompanyNovelsCreator(services.linehaulCompanyService);
  const customsBrokerNovels = customsBrokerNovelsCreator(services.customsBrokerService);
  const loadingRestrictionNovels = loadingRestrictionNovelsCreator(services.loadingRestrictionService);
  const documentNovels = documentNovelsCreator(services.documentsService, services.fileSaverService);
  const shipmentGroupNovels = shipmentGroupNovelsCreator(services.shipmentGroupService);
  const manifestNovels = manifestNovelsCreator(services.manifestService);
  const featureFlagsNovels = featureFlagsNovelsCreator(services.featureFlagsService);
  const truckTemplateNovels = truckTemplateNovelsCreator(services.truckService);

  return {
    authNovels,
    customsBrokerNovels,
    documentNovels,
    loadingRestrictionNovels,
    locationNovels,
    linehaulCompanyNovels,
    truckNovels,
    shipmentGroupNovels,
    manifestNovels,
    featureFlagsNovels,
    truckTemplateNovels,
  };
};

const addAppNovels = (
  novelMiddleware: NovelsMiddleware<StoreState, Action>,
  novels: AppNovels,
) => {
  const {
    authNovels,
    customsBrokerNovels,
    documentNovels,
    linehaulCompanyNovels,
    loadingRestrictionNovels,
    locationNovels,
    truckNovels,
    shipmentGroupNovels,
    manifestNovels,
    featureFlagsNovels,
    truckTemplateNovels,
  } = novels;

  novelMiddleware.add(
    AuthActionType.AUTHENTICATE_REQUEST,
    authNovels.authenticateNovel,
  );

  novelMiddleware.add(
    AuthActionType.LOG_OUT_REQUEST,
    authNovels.logOutNovel,
  );

  novelMiddleware.add(
    LocationActionType.SET_CURRENT_LOCATION_REQUEST,
    locationNovels.setCurrentLocationNovel,
  );

  novelMiddleware.add(
    LoadingRestrictionActionType.GET_LOADING_RESTRICTIONS_REQUEST,
    loadingRestrictionNovels.getLoadingRestrictionsNovel,
  );

  novelMiddleware.add(
    LocationActionType.GET_CURRENT_LOCATION_REQUEST,
    locationNovels.getCurrentLocationNovel,
  );

  novelMiddleware.add(
    CustomsBrokerActionType.GET_CUSTOMS_BROKERS_REQUEST,
    customsBrokerNovels.getCustomsBrokersNovel,
  );

  novelMiddleware.add(
    LinehaulCompanyActionType.GET_LINEHAUL_COMPANIES_REQUEST,
    linehaulCompanyNovels.getLinehaulCompaniesNovel,
  );

  novelMiddleware.add(
    DocumentActionType.SAVE_DOCUMENT_REQUEST,
    documentNovels.saveDocumentNovel,
  );

  novelMiddleware.add(
    DocumentActionType.UPLOAD_DOCUMENT_REQUEST,
    documentNovels.uploadDocumentNovel,
  );

  novelMiddleware.add(
    TruckTemplateActionType.GET_TRUCK_TEMPLATES_REQUEST,
    truckTemplateNovels.getTruckTemplatesNovel,
  );

  novelMiddleware.add(
    TruckTemplateActionType.CREATE_TRUCK_TEMPLATES_REQUEST,
    truckTemplateNovels.createTruckTemplateNovel,
  );

  novelMiddleware.add(
    TruckTemplateActionType.UPDATE_TRUCK_TEMPLATES_REQUEST,
    truckTemplateNovels.updateTruckTemplateNovel,
  );

  novelMiddleware.add(
    TruckTemplateActionType.DELETE_TRUCK_TEMPLATES_REQUEST,
    truckTemplateNovels.deleteTruckTemplateNovel,
  );

  novelMiddleware.add(
    TruckActionType.CREATE_TRUCK_REQUEST,
    truckNovels.createTruckNovel,
  );

  novelMiddleware.add(
    TruckActionType.DELETE_TRUCK_REQUEST,
    truckNovels.deleteTruckNovel,
  );

  novelMiddleware.add(
    TruckActionType.GET_TRUCKS_REQUEST,
    truckNovels.getTrucksNovel,
  );

  novelMiddleware.add(
    TruckActionType.GENERATE_MANIFEST_REQUEST,
    truckNovels.generateManifestNovel,
  );

  novelMiddleware.add(
    TruckActionType.PATCH_TRUCK_REQUEST,
    truckNovels.patchTruckNovel,
  );

  novelMiddleware.add(
    TruckActionType.LOAD_TRUCK_REQUEST,
    truckNovels.loadTruckNovel,
  );

  novelMiddleware.add(
    TruckActionType.LOAD_SHIPMENT_GROUP_REQUEST,
    truckNovels.loadShipmentGroupNovel,
  );

  novelMiddleware.add(
    ShipmentGroupActionType.GET_INTERNAL_SHIPMENT_GROUPS_REQUEST,
    shipmentGroupNovels.getInternalShipmentGroupsNovel,
  );

  novelMiddleware.add(
    ShipmentGroupActionType.GET_SHIPMENT_GROUP_CONFIGURATIONS_REQUEST,
    shipmentGroupNovels.getShipmentGroupConfigurationsNovel,
  );

  novelMiddleware.add(
    ShipmentGroupActionType.CREATE_SHIPMENT_GROUP_CONFIGURATION_REQUEST,
    shipmentGroupNovels.createShipmentGroupConfigurationNovel,
  );

  novelMiddleware.add(
    ShipmentGroupActionType.UPDATE_SHIPMENT_GROUP_CONFIGURATION_REQUEST,
    shipmentGroupNovels.updateShipmentGroupConfigurationNovel,
  );

  novelMiddleware.add(
    ShipmentGroupActionType.DELETE_SHIPMENT_GROUP_CONFIGURATION_REQUEST,
    shipmentGroupNovels.deleteShipmentGroupConfigurationNovel,
  );

  novelMiddleware.add(
    ManifestActionType.GET_MANIFEST_LOG_REQUEST,
    manifestNovels.getManifestEventLogNovel,
  );

  novelMiddleware.add(
    ManifestActionType.MANIFEST_SINGLE_ACTOR_REQUEST,
    manifestNovels.manifestSingleActorNovel,
  );

  novelMiddleware.add(
    FeatureFlagsActionType.GET_FEATURE_FLAGS_REQUEST,
    featureFlagsNovels.getFeatureFlagsNovel,
  );
};

export {
  createAppNovels,
  addAppNovels,
};
